import { useEffect, useRef } from "react";
import { X, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import logoFull from "@/assets/orbitrank-logo-v3.png";

interface Props {
  open: boolean;
  onClose: () => void;
}

const LiveDemoOverlay = ({ open, onClose }: Props) => {
  const navigate = useNavigate();
  const startRef = useRef<number>(0);

  // Lock scroll when open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    startRef.current = Date.now();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleStartTrial = () => {
    import("@/lib/analytics").then(({ trackEvent }) =>
      trackEvent("demo_overlay_start_trial", {
        time_in_demo: String(Math.round((Date.now() - startRef.current) / 1000)),
      }),
    );
    onClose();
    navigate("/activate");
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="OrbitRank interactive demo"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(5, 8, 14, 0.85)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        animation: "demoOverlayFade 220ms ease-out",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Top bar */}
      <div
        style={{
          flex: "0 0 auto",
          height: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 14px",
          background: "linear-gradient(180deg, rgba(10,14,22,0.95), rgba(10,14,22,0.85))",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <img src={logoFull} alt="OrbitRank" style={{ height: 22, width: "auto" }} />
          <span
            style={{
              color: "rgba(255,255,255,0.55)",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: 0.2,
              whiteSpace: "nowrap",
            }}
            className="hide-on-xs"
          >
            · Interactive Demo
          </span>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "5px 10px",
            borderRadius: 999,
            background: "rgba(0,255,136,0.08)",
            border: "1px solid rgba(0,255,136,0.25)",
          }}
        >
          <span
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: "#00FF88",
              boxShadow: "0 0 10px #00FF88",
              animation: "demoPulse 1.6s ease-in-out infinite",
            }}
          />
          <span style={{ color: "#00FF88", fontSize: 11, fontWeight: 700, letterSpacing: 0.3 }}>
            SANDBOX MODE
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            type="button"
            onClick={handleStartTrial}
            style={{
              height: 34,
              padding: "0 12px",
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 700,
              background: "linear-gradient(180deg,#00FF88,#00D97E)",
              color: "#001a0d",
              border: "none",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            <Sparkles size={12} style={{ display: "inline", marginRight: 4, verticalAlign: -1 }} />
            Free Trial
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close demo"
            style={{
              width: 34,
              height: 34,
              borderRadius: 8,
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.1)",
              color: "#fff",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Iframe */}
      <div style={{ flex: "1 1 auto", position: "relative", background: "#070B14" }}>
        <iframe
          src="/demo"
          title="OrbitRank Interactive Demo"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            border: "none",
            background: "#070B14",
          }}
          allow="clipboard-write"
        />
      </div>


      <style>{`
        @keyframes demoOverlayFade {
          from { opacity: 0; transform: scale(0.985); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes demoPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.85); }
        }
        @keyframes demoToastIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (max-width: 420px) {
          .hide-on-xs { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default LiveDemoOverlay;
